import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, ScanBarcode, X, Check, AlertCircle, FileText, Calendar, Building } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { purchaseOrdersAPI, partiesAPI, itemsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';

const CreatePurchaseOrderPage = () => {
  const { businessId, orderId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!orderId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [order, setOrder] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [newlyAddedIndex, setNewlyAddedIndex] = useState(null);
  const supplierSearchRef = useRef(null);
  const itemSearchRef = useRef(null);
  const [orderType, setOrderType] = useState('PURCHASE_ORDER'); // 'PURCHASE_ORDER' or 'VENDOR_CREDIT'
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    reference_number: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_terms: 'net_30',
    shipping_address: '',
    items: [],
    shipping_charges: 0,
    adjustment: 0,
    notes: '',
    terms_and_conditions: '',
    enable_round_off: true,
    order_type: 'PURCHASE_ORDER'
  });

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchSuppliers();
      fetchProducts();
      if (isEditing) fetchOrder();
    }
  }, [bid, orderId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target)) {
        setShowSupplierDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrdersAPI.get(orderId);
      const data = response.data;
      setOrder(data);
      setFormData(prev => ({ ...prev, ...data }));
      const supId = data.supplier_id || data.party_id;
      const sup = suppliers.find(s => (s.party_id || s.id) === supId);
      if (sup) { setSelectedSupplier(sup); setSupplierSearch(sup.name); }
      else if (data.supplier_name || data.party_name) { setSupplierSearch(data.supplier_name || data.party_name); }
    } catch (error) { toast.error(getApiErrorMessage(error, 'Error loading order')); }
    finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await partiesAPI.list({ type: 'supplier' });
      const data = response.data;
      setSuppliers(data.parties || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load suppliers')); }
  };

  const fetchProducts = async () => {
    try {
      const response = await itemsAPI.list();
      const data = response.data;
      setProducts(data.data || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load products')); }
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
    setFormData(prev => ({
      ...prev,
      supplier_id: supplier.party_id || supplier.id,
      shipping_address: supplier?.address || prev.shipping_address
    }));
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      const lineTotal = 1 * (product.purchase_price || product.cost_price || product.selling_price || 0);
      const taxRate = product.tax_rate || 0;
      const taxAmt = lineTotal * (taxRate / 100);
      
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: product.id, item_name: product.name, item_code: product.code || '',
            hsn_code: product.hsn_code || '', quantity: 1, unit: product.unit || 'Nos',
            rate: product.purchase_price || product.cost_price || product.selling_price || 0,
            discount_percent: 0, discount_amount: 0, tax_rate: taxRate,
            tax_amount: Math.round(taxAmt * 100) / 100, amount: Math.round((lineTotal + taxAmt) * 100) / 100
          }]
        };
      });
    } else {
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: '', item_name: '', item_code: '', hsn_code: '', quantity: 1, unit: 'Nos',
            rate: 0, discount_percent: 0, discount_amount: 0, tax_rate: 0, tax_amount: 0, amount: 0
          }]
        };
      });
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].item_code = product.code || '';
          newItems[index].hsn_code = product.hsn_code || '';
          newItems[index].rate = product.purchase_price || product.cost_price || product.selling_price || 0;
          newItems[index].unit = product.unit || 'Nos';
          newItems[index].tax_rate = product.tax_rate || 0;
        }
      }

      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const discountPercent = parseFloat(newItems[index].discount_percent) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;

      const lineTotal = qty * rate;
      const discountAmt = lineTotal * (discountPercent / 100);
      const taxableAmount = lineTotal - discountAmt;
      const taxAmt = taxableAmount * (taxRate / 100);

      newItems[index].discount_amount = Math.round(discountAmt * 100) / 100;
      newItems[index].tax_amount = Math.round(taxAmt * 100) / 100;
      newItems[index].amount = Math.round((taxableAmount + taxAmt) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);
  const lineDiscountTotal = formData.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const taxableAmount = subtotal - lineDiscountTotal;
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const beforeRoundOff = taxableAmount + taxTotal + formData.shipping_charges + formData.adjustment;
  const roundOff = formData.enable_round_off ? Math.round(beforeRoundOff) - beforeRoundOff : 0;
  const grandTotal = beforeRoundOff + roundOff;

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.supplier_id) errors.supplier = 'Supplier is required';
    if (!formData.order_date) errors.order_date = 'Order date is required';
    if (formData.items.length === 0) errors.items = 'At least one item is required';
    else {
      formData.items.forEach((item, index) => {
        if (!item.item_id) errors[`item_${index}_product`] = 'Select a product';
        if (!item.quantity || item.quantity <= 0) errors[`item_${index}_quantity`] = 'Quantity must be > 0';
      });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (asDraft = false) => {
    setValidationErrors({});
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...formData,
        subtotal,
        discount_total: lineDiscountTotal,
        taxable_amount: taxableAmount,
        tax_total: taxTotal,
        grand_total: grandTotal,
        save_as_draft: asDraft,
        supplier_name: selectedSupplier?.name || '',
        supplier_phone: selectedSupplier?.mobile || '',
        supplier_email: selectedSupplier?.email || '',
        supplier_gstin: selectedSupplier?.gstin || ''
      };

      let response;
      if (isEditing) {
        response = await purchaseOrdersAPI.update(orderId, payload);
      } else {
        response = await purchaseOrdersAPI.create(payload);
      }
      const data = response.data;

      setSuccess('Purchase order saved successfully!');
      setTimeout(() => navigate(`/business/${bid}/purchase-orders/${data.order?.id || orderId}`), 1000);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Error saving order'));
    }
    finally { setSaving(false); }
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    s.mobile?.includes(supplierSearch) ||
    s.email?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/purchase-orders`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEditing ? 'Edit Purchase Order' : 'New Purchase Order'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Create and manage supplier orders</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleSave(true)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button 
                onClick={() => handleSave(false)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
              >
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Confirm Order'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">
              
              {/* Error/Success Messages */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                  <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {success && (
                <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <p className="text-sm text-teal-700">{success}</p>
                </div>
              )}

              {/* SUPPLIER DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Supplier Details</h2>
                    <p className="text-[11px] text-slate-500">Search and select supplier</p>
                  </div>
                </div>
                
                <div className="relative" ref={supplierSearchRef}>
                  <label className={`block text-[12px] font-medium mb-2 ${validationErrors.supplier ? 'text-red-500' : 'text-slate-600'}`}>
                    Supplier {validationErrors.supplier && <span className="text-red-500 ml-1">• Required</span>}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text" value={supplierSearch}
                      onChange={(e) => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); }}
                      onFocus={() => setShowSupplierDropdown(true)}
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50 border-2 rounded-xl text-[14px] focus:bg-white focus:ring-0 focus:outline-none transition-all duration-200 ${
                        validationErrors.supplier 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-slate-200 focus:border-teal-500'
                      }`}
                      placeholder="Search by name, phone, or email..."
                    />
                  </div>
                  
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredSuppliers.slice(0, 8).map((supplier, idx) => {
                        const contact = [supplier.mobile, supplier.email].filter(Boolean).join(' • ');
                        return (
                          <div key={supplier.party_id || supplier.id}
                            onClick={() => handleSupplierSelect(supplier)}
                            className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{supplier.name}</p>
                              {contact && <p className="text-[12px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedSupplier && (() => {
                  const selContact = [selectedSupplier.mobile, selectedSupplier.email].filter(Boolean).join(' • ');
                  return (
                    <div className="mt-4 p-3 bg-teal-50/50 rounded-lg border border-teal-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedSupplier.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button onClick={() => { setSelectedSupplier(null); setSupplierSearch(''); setFormData(prev => ({ ...prev, supplier_id: '' })); }}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Order Items</h2>
                      <p className="text-[11px] text-slate-500">{formData.items.length} item{formData.items.length !== 1 ? 's' : ''} added</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-[14px] focus:border-teal-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search items by name or code..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map((product, idx) => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[12px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[14px] font-medium text-slate-600">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Search above or click "Add Line" to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">HSN/SAC</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rate</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Disc %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                          <th className="px-4 py-3 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formData.items.map((item, index) => (
                          <tr key={`item-${index}-${item.item_id || 'new'}`} 
                              className={`hover:bg-slate-50/80 transition-colors duration-150 ${
                                index === newlyAddedIndex ? 'animate-lineItemSlideIn animate-rowHighlight' : ''
                              }`}>
                            <td className="px-4 py-3 text-[13px] text-slate-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              <select value={item.item_id} onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500 focus:bg-white transition-all">
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.hsn_code || ''} onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                                className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" placeholder="—" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-teal-500 focus:bg-white transition-all" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.rate} onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.discount_percent} onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" max="100" placeholder="%" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-teal-500 focus:bg-white transition-all" min="0" placeholder="%" />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[14px] font-semibold text-teal-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button onClick={() => removeItem(index)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 active:scale-95">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* TERMS & NOTES Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Terms & Notes</h2>
                    <p className="text-[11px] text-slate-500">Additional information</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Notes</label>
                    <textarea value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                      rows={3} placeholder="Add notes..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Payment Terms</label>
                    <select value={formData.payment_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none transition-all duration-200">
                      <option value="due_on_receipt">Due on Receipt</option>
                      <option value="net_15">Net 15</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_45">Net 45</option>
                      <option value="net_60">Net 60</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-12 lg:col-span-4 space-y-4">
              
              {/* ORDER DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Details</h2>
                    <p className="text-[11px] text-slate-500">Configure order settings</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Order Type Selector */}
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Order Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setOrderType('PURCHASE_ORDER');
                          setFormData(prev => ({ ...prev, order_type: 'PURCHASE_ORDER' }));
                        }}
                        className={`px-3 py-2.5 rounded-lg text-[12px] font-medium transition-all duration-200 border-2 ${
                          orderType === 'PURCHASE_ORDER'
                            ? 'bg-teal-50 border-teal-500 text-teal-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        Purchase Order
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOrderType('VENDOR_CREDIT');
                          setFormData(prev => ({ ...prev, order_type: 'VENDOR_CREDIT' }));
                        }}
                        className={`px-3 py-2.5 rounded-lg text-[12px] font-medium transition-all duration-200 border-2 ${
                          orderType === 'VENDOR_CREDIT'
                            ? 'bg-amber-50 border-amber-500 text-amber-700'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        Vendor Credit
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">
                      {orderType === 'VENDOR_CREDIT' ? 'Credit Number' : 'PO Number'}
                    </label>
                    <input type="text" value={order?.order_number || 'Auto-generated'} disabled
                      className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-[14px] text-slate-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-2">
                        {orderType === 'VENDOR_CREDIT' ? 'Credit Date' : 'Order Date'}
                      </label>
                      <input type="date" value={formData.order_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">
                        {orderType === 'VENDOR_CREDIT' ? 'Due Date' : 'Expected Delivery'}
                      </label>
                      <input type="date" value={formData.expected_delivery_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reference Number</label>
                    <input type="text" value={formData.reference_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                      placeholder={orderType === 'VENDOR_CREDIT' ? 'Debit note reference' : 'Supplier quotation ref'}
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY Card - Premium Teal Gradient */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Summary</h2>
                    <p className="text-[11px] text-slate-500">Final amounts</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Taxable Amount</span>
                    <span className="text-slate-800">₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-teal-600 font-medium">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-teal-200 pt-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[20px] font-bold text-teal-600">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                    <input type="checkbox" checked={formData.enable_round_off}
                      onChange={(e) => setFormData(prev => ({ ...prev, enable_round_off: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-[12px] text-slate-600 group-hover:text-slate-800 transition-colors">Enable round off</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePurchaseOrderPage;
